import { LocalizationProvider } from '@localization/LocalizationProvider';
import styles from './QuantityUnitsComponent.module.scss';

import { CdfGrid } from '@commonComponents/data/grid/CdfGrid/CdfGrid.tsx';
import { AG_GRID_LOCALE_DE, AG_GRID_LOCALE_EN } from '@ag-grid-community/locale';
import { GridApi } from 'ag-grid-enterprise';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { RowSelectionMode } from '@models/enums/RowSelectionMode';
import { PermissionProvider } from '@auth/PermissionProvider.tsx';
import { useInjectReducers } from '@hooks/useInjectReducers.ts';
import { ApiCallType } from '@api/ApiCalls.ts';
import { useQuantityUnitsPermissions } from '@hooks/permissions/useQuantityUnitsPermissions.ts';
import { QuantityUnit } from '@models/DomainModels/Master/quantity-unit.ts';
import { gridColumnDefinitions } from './grid-column-definitions';
import { DynamicStore } from '@state/interfaces/idynamic-store';
import classNames from 'classnames';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from '@commonComponents/core/ErrorFallback';
import { logger } from '@utils/logging-utils';
import type { EntityDialogCloseResult } from '@commonComponents/dialogs/EntityDetailDialog/useEntityDetail';
// MUI components
import { ThemeProvider } from '@mui/material/styles';
import Grid from '@mui/material/Grid2';
import brzTheme from '@brz/brz-global-mui-theme';
import { Typography } from '@mui/material';
import { QuantityUnitDetailDialogWrapper } from './QuantityUnitDetailDialog';

/**
 * Props interface for QuantityUnitsComponent
 */
interface QuantityUnitsComponentProps {
  /** Organization ID for permission checks and API calls */
  readonly organizationId: string;
  /** Redux store for state management */
  readonly store: DynamicStore | undefined;
}

const extendedAG_GRID_LOCALE_DE = {
  ...AG_GRID_LOCALE_DE,
  ariaHeaderSelection: ''
};

/**
 * Component for managing quantity units
 * Displays a grid of quantity units with CRUD operations
 */
function QuantityUnitsComponent({ organizationId, store }: Readonly<QuantityUnitsComponentProps>) {
  const { writePermission } = useQuantityUnitsPermissions(organizationId);

  const { t, i18n } = useTranslation(['quantityUnits', 'common']);
  const [localeText, setLocaleText] = useState(AG_GRID_LOCALE_EN);
  const [gridKey, setGridKey] = useState(0);
  const gridApiRef = useRef<GridApi | null>(null);
  const gridRef = useRef<{
    refresh: () => void;
    addCreatedItem: (item: QuantityUnit) => void;
    updateModifiedItem: (item: QuantityUnit) => void;
  }>(null);
  const selectedIdsRef = useRef<string[]>([]);

  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedQuantityUnitData, setSelectedQuantityUnitData] = useState<QuantityUnit | undefined>(undefined);

  /**
   * Handle language change and update the grid locale
   */
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const newLocaleText = lng === 'de' ? extendedAG_GRID_LOCALE_DE : AG_GRID_LOCALE_EN;
      setLocaleText(newLocaleText);
      setGridKey(prevKey => prevKey + 1);
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  /**
   * Grid ready callback to store the grid API reference
   */
  const onGridReady = useCallback((params: { api: GridApi }) => {
    gridApiRef.current = params.api;
  }, []);

  /**
   * Handle clicking on a quantity unit profile
   * @param data The quantity unit data
   */
  const handleProfileClick = useCallback((data: QuantityUnit) => {
    if (!data?.id) {
      logger.warn('QuantityUnitsComponent', 'Profile click with invalid data', data);
      return;
    }

    // Store the selected ID for later use if needed
    selectedIdsRef.current = [data.id];
    setSelectedQuantityUnitData(data);
    setProfileDialogOpen(true);
  }, []);

  /**
   * Handle profile dialog close and update the grid using transactions
   */
  const handleProfileClose = useCallback(async (result: EntityDialogCloseResult<QuantityUnit>) => {
    setProfileDialogOpen(false);

    // Update the grid using transactions instead of full refresh
    if (result.action === 'created' && result.data && gridRef.current) {
      gridRef.current.addCreatedItem(result.data);
    } else if (result.action === 'updated' && result.data && gridRef.current) {
      gridRef.current.updateModifiedItem(result.data);
    }

    setSelectedQuantityUnitData(undefined);
  }, []);

  /**
   * Memoized column definitions to prevent unnecessary re-renders
   */
  const columnDefs = useCallback(() => gridColumnDefinitions({ t }), [t]);

  /**
   * Handle clicking the add button to create a new quantity unit
   */
  const handleAddButtonClick = useCallback(() => {
    setSelectedQuantityUnitData(undefined);
    setProfileDialogOpen(true);
  }, []);

  return (
    <Grid className={styles.quantityUnitsComponent}>
      <Typography variant="h4" component="h1">
        {t('title')}
      </Typography>
      <Grid className={styles.content}>
        <Grid className={classNames(styles.grid)}>
          <CdfGrid<QuantityUnit>
            ref={gridRef}
            apiCallType={ApiCallType.FetchQuantityUnits}
            key={gridKey}
            columnDefs={columnDefs()}
            gridOptions={{ onGridReady, localeText }}
            rowSelection={RowSelectionMode.Multiple}
            showAddButton={writePermission}
            showDeleteButton={writePermission}
            showDeleteButtonInGrid={writePermission}
            onAddClick={handleAddButtonClick}
            addButtonText={t('addButtonText')}
            onDoubleClick={event => event.data && handleProfileClick(event.data)}
          />
        </Grid>
      </Grid>
      {profileDialogOpen && (
        <QuantityUnitDetailDialogWrapper
          quantityUnitData={selectedQuantityUnitData}
          open={profileDialogOpen}
          onClose={handleProfileClose}
          organizationId={organizationId}
          store={store}
        />
      )}
    </Grid>
  );
}

/**
 * Wrapper component for QuantityUnitsComponent
 * Provides necessary context providers and error boundaries
 */
export function QuantityUnitsComponentWrapper({ organizationId, store }: Readonly<QuantityUnitsComponentProps>) {
  const reducerInjected = useInjectReducers(store, organizationId);
  if (!reducerInjected) {
    return null;
  }

  return (
    <ThemeProvider theme={brzTheme}>
      <LocalizationProvider>
        <Provider store={store}>
          <PermissionProvider>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <QuantityUnitsComponent organizationId={organizationId} store={store} />
            </ErrorBoundary>
          </PermissionProvider>
        </Provider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
